import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://dakhla.pk';

/**
 * Custom hook to dynamically manage page-specific title, meta description,
 * canonical URL, OpenGraph, Twitter card tags, and optional Schema.org JSON-LD structured data.
 *
 * @param {string} title The document title
 * @param {string} description The meta description content
 * @param {string} [image] Optional og:image / twitter:image URL
 * @param {object} [schema] Optional Schema.org JSON-LD object
 */
const useDocumentMetadata = (title, description, image, schema) => {
  const location = useLocation();

  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${location.pathname === '/' ? '/' : location.pathname}`;

    // --- Title ---
    if (title) {
      document.title = title;
    }

    // Helper to set or create meta tags by name or property
    const setMetaTag = (attrName, attrVal, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attrName}="${attrVal}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // --- Meta Description ---
    if (description) {
      setMetaTag('name', 'description', description);
      setMetaTag('property', 'og:description', description);
      setMetaTag('name', 'twitter:description', description);
      setMetaTag('property', 'twitter:description', description);
    }

    // --- Social Titles ---
    if (title) {
      setMetaTag('property', 'og:title', title);
      setMetaTag('name', 'twitter:title', title);
      setMetaTag('property', 'twitter:title', title);
    }

    // --- Canonical URL ---
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;

    // --- URLs ---
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('name', 'twitter:url', canonicalUrl);
    setMetaTag('property', 'twitter:url', canonicalUrl);

    // --- Social Images ---
    if (image) {
      const fullImageUrl = image.startsWith('http') ? image : `${SITE_URL}${image.startsWith('/') ? '' : '/'}${image}`;
      setMetaTag('property', 'og:image', fullImageUrl);
      setMetaTag('name', 'twitter:image', fullImageUrl);
      setMetaTag('property', 'twitter:image', fullImageUrl);
    }

    // --- JSON-LD Schema ---
    let schemaScript = document.getElementById('dakhla-dynamic-schema');
    if (schema) {
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'dakhla-dynamic-schema';
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    } else if (schemaScript) {
      schemaScript.remove();
    }

    return () => {
      // Clean up dynamic schema on unmount if any
      const existing = document.getElementById('dakhla-dynamic-schema');
      if (existing) {
        existing.remove();
      }
    };
  }, [title, description, image, schema, location.pathname]);
};

export default useDocumentMetadata;
